/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { PAYMENT_METHODS, type PaymentMethod } from "./types";
import { money, todayIso } from "./format";

export type Payment = {
  paid_on: string;
  paid_amount: number;
  payment_method: PaymentMethod;
  payment_reference: string | null;
};

type Props = {
  total: number;
  currency: string | null;
  saving: boolean;
  onClose: () => void;
  onConfirm: (payment: Payment) => void;
};

// When, how much and how an invoice was paid. Defaults: today, the full
// total, bank transfer.
export const MarkPaidModal = defineComponent<Props>({
  name: "MarkPaidModal",
  props: ["total", "currency", "saving", "onClose", "onConfirm"],
  setup(props) {
    const { t } = useLocalization();
    const paidOn = ref(todayIso());
    const amount = ref<number>(props.total);
    const method = ref<PaymentMethod>("bank_transfer");
    const reference = ref("");

    return () => (
      <LemmonModal
        title={t("inventory.invoices.mark_paid_title")}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose()}>
                {t("general.words.cancel")}
              </button>
              <button
                class="btn btn-primary"
                disabled={props.saving || !(amount.value >= 0) || !paidOn.value}
                onClick={() =>
                  props.onConfirm({
                    paid_on: paidOn.value,
                    paid_amount: Number(amount.value),
                    payment_method: method.value,
                    payment_reference: reference.value.trim() || null,
                  })
                }
              >
                <span class="material-symbols-outlined text-base">check_circle</span>
                {t("inventory.invoices.mark_paid")}
              </button>
            </div>
          </div>
        )}
      >
        <div class="space-y-4 p-6">
          <div class="rounded-lg bg-base-200 p-4 text-center">
            <div class="text-xs uppercase tracking-wide text-base-content/50">{t("inventory.invoices.amount_due")}</div>
            <div class="text-3xl font-semibold">{money(props.total, props.currency)}</div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <label class="block">
              <span class="mb-1 block text-sm font-medium">{t("inventory.invoices.paid_on")}</span>
              <input type="date" class="input input-bordered input-sm w-full" v-model={paidOn.value} />
            </label>
            <label class="block">
              <span class="mb-1 block text-sm font-medium">{t("inventory.invoices.paid_amount")}</span>
              <input
                type="number"
                step="0.01"
                min="0"
                class="input input-bordered input-sm w-full text-right"
                value={amount.value}
                onInput={(e: Event) => (amount.value = Number((e.target as HTMLInputElement).value))}
              />
            </label>
          </div>
          <div>
            <span class="mb-1 block text-sm font-medium">{t("inventory.invoices.payment_method")}</span>
            <div class="flex flex-wrap gap-2">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  class={["btn btn-sm", method.value === m ? "btn-primary" : "btn-ghost border-base-300"].join(" ")}
                  onClick={() => (method.value = m)}
                >
                  {t(`inventory.invoices.method_${m}`)}
                </button>
              ))}
            </div>
          </div>
          <label class="block">
            <span class="mb-1 block text-sm font-medium">{t("inventory.invoices.payment_reference")}</span>
            <input
              class="input input-bordered input-sm w-full"
              placeholder={t("inventory.invoices.payment_reference_placeholder")}
              v-model={reference.value}
            />
          </label>
        </div>
      </LemmonModal>
    );
  },
});
